import { useRef } from 'react';
import { ArrowRightIcon, ExpandIcon } from './IconSet';
import { placeholderImage } from '../propertyConfig';

const SWIPE_THRESHOLD_PX = 40;

// The actual photo-stage markup (main image, prev/next, thumbnail strip,
// swipe handling) - shared by the small inline gallery on the property page
// and the fullscreen Images tab (PropertyFullscreenGallery.js), so both
// stay in sync and don't duplicate this interaction logic. `size` only
// changes the CSS treatment; the behavior is identical at both sizes.
// How many real thumbnails show before the "+N more" tile takes over the
// last grid cell (size="default" only - the fullscreen gallery shows every
// thumbnail, uncapped).
const VISIBLE_THUMB_COUNT = 4;

function PropertyGalleryStage({
  images = [],
  activeIndex = 0,
  onActiveIndexChange,
  altText = '',
  size = 'default',
  overlayActions = null,
  onExpand = null,
}) {
  const touchStartXRef = useRef(null);
  const activeImage = images[Math.min(activeIndex, images.length - 1)];

  const showPrevImage = () => {
    onActiveIndexChange((current) => (current === 0 ? images.length - 1 : current - 1));
  };

  const showNextImage = () => {
    onActiveIndexChange((current) => (current === images.length - 1 ? 0 : current + 1));
  };

  const handleTouchStart = (event) => {
    touchStartXRef.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event) => {
    if (touchStartXRef.current === null) {
      return;
    }
    const deltaX = event.changedTouches[0].clientX - touchStartXRef.current;
    touchStartXRef.current = null;
    if (deltaX > SWIPE_THRESHOLD_PX) {
      showPrevImage();
    } else if (deltaX < -SWIPE_THRESHOLD_PX) {
      showNextImage();
    }
  };

  // "+N more" tile only makes sense where there's an expand action to send
  // people to (size="default", called from the property page) - the
  // fullscreen gallery itself has nowhere further to send you, so it keeps
  // showing every thumbnail uncapped.
  const capThumbs = Boolean(onExpand);
  const visibleThumbs = capThumbs ? images.slice(0, VISIBLE_THUMB_COUNT) : images;
  const hiddenCount = capThumbs ? images.length - VISIBLE_THUMB_COUNT : 0;

  return (
    <div className={`property-gallery-stage property-gallery-stage--${size}`}>
      <div
        className="property-gallery-main-wrap"
        onTouchStart={images.length > 1 ? handleTouchStart : undefined}
        onTouchEnd={images.length > 1 ? handleTouchEnd : undefined}
      >
        {images.length > 1 && (
          <span className="property-gallery-counter">
            {activeIndex + 1} / {images.length}
          </span>
        )}
        {overlayActions}
        <img
          src={activeImage || placeholderImage}
          alt={altText}
          className="property-gallery-main"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = placeholderImage;
          }}
        />
        {images.length > 1 && (
          <>
            <button
              type="button"
              className="property-gallery-nav property-gallery-nav--prev"
              aria-label="Previous image"
              onClick={showPrevImage}
            >
              <ArrowRightIcon size={18} />
            </button>
            <button
              type="button"
              className="property-gallery-nav property-gallery-nav--next"
              aria-label="Next image"
              onClick={showNextImage}
            >
              <ArrowRightIcon size={18} />
            </button>
          </>
        )}
        {onExpand && (
          <button type="button" className="property-gallery-view-all" onClick={onExpand}>
            <ExpandIcon size={15} /> View all photos
          </button>
        )}
      </div>

      {images.length > 1 && (
        <div className="property-gallery-thumbs" role="list" aria-label="Property images">
          {visibleThumbs.map((image, index) => {
            const isLastVisible = capThumbs && index === VISIBLE_THUMB_COUNT - 1 && hiddenCount > 0;
            return (
              <button
                key={`gallery-thumb-${index}`}
                type="button"
                className={`property-thumb ${index === activeIndex ? 'active' : ''}`}
                aria-label={isLastVisible ? `View all ${images.length} photos` : `View image ${index + 1}`}
                onClick={isLastVisible ? onExpand : () => onActiveIndexChange(index)}
              >
                <img
                  src={image}
                  alt={`${altText} - photo ${index + 1}`}
                  loading="lazy"
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = placeholderImage;
                  }}
                />
                {isLastVisible && (
                  <span className="property-thumb__more-overlay">+{hiddenCount}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PropertyGalleryStage;
